'use client';

import React from 'react';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';
import { Button } from '@/shared/ui/shadcn/button';
import { NoticeBanner } from '@/shared/ui/NoticeBanner';
import { Progress } from '@/shared/ui/shadcn/progress';

interface UpgradePromptProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string;
  feature?: string;
  currentLimit?: number;
  currentUsage?: number;
}

export function UpgradePrompt({
  isOpen,
  onClose,
  title,
  message,
  feature,
  currentLimit,
  currentUsage,
}: UpgradePromptProps) {
  const router = useRouter();
  const t = useTranslations('upgradePrompt');
  const tCommon = useTranslations('common');

  const handleUpgrade = () => {
    // Tokens screen is the single monetization surface after the pivot —
    // legacy plan pages were removed.
    router.push('/account/tokens');
    onClose();
  };

  const featureItems: Record<string, string[]> = {
    projects: ['projects_1', 'projects_2', 'projects_3'],
    generations: ['generations_1', 'generations_2', 'generations_3'],
    angles: ['angles_1', 'angles_2', 'angles_3'],
    references: ['references_1', 'references_2', 'references_3'],
    remove_watermark: ['watermark_1', 'watermark_2', 'watermark_3'],
    sketches: ['sketches_1', 'sketches_2', 'sketches_3'],
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="sr-only">{message}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <NoticeBanner
            variant="warning"
            title={message}
            message={
              currentLimit !== undefined && currentUsage !== undefined ? (
                <div className="space-y-2">
                  <div className="flex justify-between text-form-help">
                    <span>{t('currentUsage')}</span>
                    <span className="font-medium">
                      {currentUsage} / {currentLimit}
                    </span>
                  </div>
                  <Progress
                    value={Math.min(100, (currentUsage / currentLimit) * 100)}
                    className="h-2"
                  />
                </div>
              ) : undefined
            }
          />

          {feature && featureItems[feature] && (
            <NoticeBanner
              variant="info"
              title={t('upgradeToUnlock')}
              message={
                <ul className="space-y-1">
                  {featureItems[feature].map((key) => (
                    <li key={key}>• {t(`features.${key}`)}</li>
                  ))}
                </ul>
              }
            />
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>
            {tCommon('cancel')}
          </Button>
          <Button onClick={handleUpgrade}>{t('upgradePlan')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
